import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { SatError } from "@/features/cargar-cfdi/types";
import { solicitudesApi } from "@/features/solicitudes/services/solicitudes.api";
import { qk } from "@/lib/query-keys";
import { toastError } from "@/lib/toast";
import type { SolicitudDetalle } from "@/lib/types";

/**
 * Orquestador del flujo "estoy esperando que SAT valide el CFDI":
 *   1. `start()` activa el polling (1s) sobre la solicitud.
 *   2. Cuando la solicitud transita a `Validado`, se llama `onValidado`
 *      y se apaga el polling.
 *   3. Si la solicitud transita a `RechazadoFiscal` o `SuspendidaFiscal`,
 *      se materializa el `SatError` y se llama `onErrorSat`.
 *
 * El polling comparte cache con `useSolicitud` (suspense) · ambos
 * observan la misma queryKey, no hay dobles fetches.
 */
export function useSatValidationFlow(
  solicitudId: string,
  options: {
    onValidado: () => void;
    onErrorSat: (err: SatError) => void;
  }
) {
  const [esperando, setEsperando] = useState(false);

  // Polling on-demand · `useQuery` (no suspense) que solo refetcha
  // cuando `esperando=true`.
  const sol = useQuery<SolicitudDetalle>({
    queryKey: qk.solicitud(solicitudId),
    queryFn: () => solicitudesApi.obtener(solicitudId),
    refetchInterval: esperando ? 1_000 : false,
    enabled: esperando,
  });

  useEffect(() => {
    if (!esperando || !sol.data) return;
    const estado = sol.data.estado;
    if (estado === "Validado") {
      setEsperando(false);
      options.onValidado();
      return;
    }
    if (estado === "RechazadoFiscal" || estado === "SuspendidaFiscal") {
      setEsperando(false);
      const cfdi = sol.data.cfdi;
      const mensaje =
        cfdi?.statusFiscalMensajeAmigable ??
        cfdi?.statusFiscalMensajeOriginal ??
        (estado === "RechazadoFiscal"
          ? "El SAT rechazó este CFDI · revisa con el emisor."
          : "El SAT no respondió o no se pudo consultar el CFDI.");
      const err: SatError = {
        estado,
        codigo: cfdi?.statusFiscalCodigo ?? null,
        mensaje,
      };
      options.onErrorSat(err);
      toastError({ title: estado === "RechazadoFiscal" ? "SAT rechazó el CFDI" : "No se pudo validar con el SAT", message: mensaje });
    }
  }, [esperando, sol.data, options]);

  return {
    esperando,
    start: () => setEsperando(true),
    reset: () => setEsperando(false),
  };
}
